import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import MaterialLink from '@material-ui/core/Link';

import * as Store from '@apply/store';

import { Header } from './Header';
import { alertModal } from '@apply/ui';
import { ChangePasswordResponse } from 'libs/store/src/cross';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

export interface Actions {
    resetPassword?: (id: string, resetToken: string, password: string) => Promise<ChangePasswordResponse>;
}
export interface ResetPasswordPageProps {
    actions?: Actions;
}

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ actions }) => {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const id = params.get('id');
    const code = params.get('code');

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string>('');
    const [error, setError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [passwordResetResult, setPasswordResetResult] = useState<ChangePasswordResponse>(null);

    const [successful, setSuccessful] = useState<boolean>(false);

    const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [passwordConfirm, setPasswordConfirm] = useState<string>('');

    const mounted = useRef<boolean>(true);

    useEffect(() => {
        setError(false);
        setErrorMessage('');
        setPasswordResetResult(null);

        if (!password || !passwordConfirm) {
            return;
        }

        if (password && passwordConfirm && password !== passwordConfirm) {
            setError(true);
            setErrorMessage('passwords do not match');
            return;
        }

        if (password && password.length < 8) {
            setError(true);
            setErrorMessage('password must be at least 8 characters long');
            return;
        }
    }, [password, passwordConfirm]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const handlePasswordReset = async () => {
        setSubmitting(true);

        try {
            //why
            const result = await actions.resetPassword(id, code, password);
            if (mounted) {
                setSubmitting(false);

                if (result) {
                    if (result.errors && result.errors.length > 0) {
                        // Some other error in the errors collection delegate it to catch.
                        throw new Error(result.errors.join('; '));
                    }

                    if (result.result) {
                        if (result && result.result && result.result.succeeded) {
                            setSuccessful(true);
                        } else {
                            const {
                                passwordTooShort,
                                passwordRequiresMixedCase,
                                passwordRequiresDigit,
                                passwordRequiresSpecialCharacter,
                            } = result.result;
                            if (
                                passwordTooShort ||
                                passwordRequiresMixedCase ||
                                passwordRequiresDigit ||
                                passwordRequiresSpecialCharacter
                            ) {
                                setPasswordResetResult(result);
                            }
                        }
                    } else {
                        throw new Error('An unexpected error occurred and your password could not be reset. Please try again.');
                    }
                } else {
                    throw new Error('An unexpected error occurred and your password could not be reset. Please try again.');
                }
            }
        } catch (err) {
            // istanbul ignore else
            if (mounted) {
                //Error message could contain multiple errors seperated by ;. If so
                //put into a <ul><li>... and show all the errors ???
                setSubmitting(false);
                setSubmitError(err.message);
            }
        }
    };

    const handleTogglePasswordVisible = () => {
        setPasswordVisible((prev) => !prev);
    };

    useEffect(() => {
        if (submitError) {
            alertModal({
                message: 'An error has occurred while communicating with our server.  Please try your operation again.',
            });
            setSubmitError(undefined);
        }
    }, [submitError]);

    if (successful) {
        return <Navigate to={'/login'} />;
    }

    return (
        <>
            <Header />
            <div className='app-container login apply-login-container'>
                <h2>Create New Password</h2>
                <main>
                    <div className='simple-form-container one-column'>
                        <div className='columns form-content'>
                            <div className='standard-form'>
                                <div className='row'>
                                    <div className='columns small-12 large-12'>
                                        <label htmlFor='password'>
                                            Password
                                            <span className='req'>*</span>
                                        </label>
                                        <div className='has-show-hide'>
                                            <input
                                                id='password'
                                                data-testid='resetpassword-password'
                                                type={passwordVisible ? 'text' : 'password'}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                name='password'
                                                required
                                            />
                                            <button
                                                type='button'
                                                data-testid='resetpassword-show-password'
                                                className='show-hide'
                                                onClick={handleTogglePasswordVisible}
                                            >
                                                <FontAwesomeIcon icon={faEyeSlash} />
                                            </button>
                                        </div>
                                        <span className='helper'>
                                            <span className='tooltip-trigger'>
                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                            </span>
                                            <span className='text text-wide'>
                                                Enter your new password. It must meet the following criteria:
                                                <br />
                                                <ul>
                                                    <li>At least 8 characters</li>
                                                    <li>A mixture of both uppercase and lowercase letters</li>
                                                    <li>A mixture of letters and numbers</li>
                                                    <li>Inclusion of at least one special character, e.g., ! @ # ? ]</li>
                                                </ul>
                                            </span>
                                        </span>
                                    </div>

                                    <div className='columns small-12 large-12'>
                                        <label htmlFor='password'>
                                            Confirm Password
                                            <span className='req'>*</span>
                                        </label>
                                        <div className='has-show-hide'>
                                            <input
                                                id='password'
                                                data-testid='resetpassword-confirm-password'
                                                type={passwordVisible ? 'text' : 'password'}
                                                value={passwordConfirm}
                                                onChange={(e) => setPasswordConfirm(e.target.value)}
                                                name='password'
                                                required
                                            />
                                            <button
                                                type='button'
                                                data-testid='resetpassword-confirm-show-password'
                                                className='show-hide'
                                                onClick={handleTogglePasswordVisible}
                                            >
                                                <FontAwesomeIcon icon={faEyeSlash} />
                                            </button>
                                        </div>
                                        <span className='helper'>
                                            <span className='tooltip-trigger'>
                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                            </span>
                                            <span className='text'>Confirm your new password</span>
                                        </span>
                                    </div>
                                    {error && (
                                        <div className='columns small-12 large-12'>
                                            <Typography style={{ color: 'red' }}>{errorMessage}</Typography>
                                        </div>
                                    )}
                                    {passwordResetResult && passwordResetResult.result && (
                                        <div
                                            className='columns small-12 large-12'
                                            style={{ fontSize: '12px' }}
                                        >
                                            Your password failed to meet the requirements indicated in{' '}
                                            <span style={{ color: 'red' }}>red</span> below...
                                            <ul>
                                                <li
                                                    style={{
                                                        color: passwordResetResult.result.passwordTooShort ? 'red' : 'inherit',
                                                    }}
                                                >
                                                    At least 8 characters
                                                </li>
                                                <li
                                                    style={{
                                                        color: passwordResetResult.result.passwordRequiresMixedCase
                                                            ? 'red'
                                                            : 'inherit',
                                                    }}
                                                >
                                                    A mixture of both uppercase and lowercase letters
                                                </li>
                                                <li
                                                    style={{
                                                        color: passwordResetResult.result.passwordRequiresDigit
                                                            ? 'red'
                                                            : 'inherit',
                                                    }}
                                                >
                                                    A mixture of letters and numbers
                                                </li>
                                                <li
                                                    style={{
                                                        color: passwordResetResult.result.passwordRequiresSpecialCharacter
                                                            ? 'red'
                                                            : 'inherit',
                                                    }}
                                                >
                                                    Inclusion of at least one special character, e.g., ! @ # ? ]
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                    <div className='columns small-12 large-12'>
                                        <button
                                            data-testid='resetpassword-button'
                                            disabled={submitting || error || !password || !passwordConfirm}
                                            onClick={handlePasswordReset}
                                            className='button signin-button'
                                        >
                                            Create Password
                                        </button>
                                    </div>
                                    <div className='columns small-6 large-6'>
                                        <MaterialLink
                                            component={Link}
                                            to='/login'
                                        >
                                            Login
                                        </MaterialLink>
                                    </div>
                                    <div className='columns small-6 large-6 text-right'>
                                        <MaterialLink
                                            component={Link}
                                            to='/register'
                                        >
                                            Register
                                        </MaterialLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            resetPassword: (id: string, resetToken: string, password: string) =>
                dispatch(Store.CrossStore.resetPassword(id, resetToken, password)),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(ResetPasswordPage);
